import type { ReportDef } from "./types";

const T = "text" as const;
const M = "money" as const;
const D = "date" as const;
const I = "int" as const;

/** Every firm-wide report + the deferred/per-client cards, keyed by URL slug.
 * Columns mirror the milestone §6 layout and reference the server row keys; the
 * `sortKey` on a column is the value the backend's per-report allow-list accepts. */
export const REPORT_CATALOG: Record<string, ReportDef> = {
  "caf-monitoring": {
    key: "caf-monitoring",
    title: "CAF Monitoring",
    description: "Pending POA authorizations at the CAF Unit; flags those pending 11+ weeks.",
    group: "Monitoring",
    columns: [
      { key: "taxpayer", label: "Taxpayer", kind: T, sortKey: "taxpayer" },
      { key: "form", label: "Form", kind: T, sortKey: "form" },
      { key: "tax_year", label: "Tax Year", kind: I, sortKey: "tax_year" },
      { key: "date_sent", label: "Date Sent", kind: D, sortKey: "date_sent" },
      { key: "last_check", label: "Last Check", kind: D, sortKey: "last_check" },
      { key: "weeks_diff", label: "Weeks Diff", kind: I, sortKey: "weeks_diff", align: "right" },
      { key: "primary_practitioner", label: "Primary Practitioner", kind: T, sortKey: "primary_practitioner" },
    ],
  },
  "transcript-monitoring": {
    key: "transcript-monitoring",
    title: "Transcript Monitoring",
    description: "Standing transcript monitors with a status overview of CAF and monitor checks.",
    group: "Monitoring",
    layout: "transcript-monitoring",
    columns: [
      { key: "client", label: "Client", kind: T, sortKey: "client" },
      { key: "form", label: "Form", kind: T, sortKey: "form" },
      { key: "year_from", label: "From", kind: I, sortKey: "year_from" },
      { key: "year_to", label: "To", kind: I, sortKey: "year_to" },
      { key: "primary_practitioner", label: "Primary Practitioner", kind: T, sortKey: "primary_practitioner" },
    ],
  },
  "upcoming-cseds": {
    key: "upcoming-cseds",
    title: "Upcoming CSEDs",
    description: "Collection-statute expirations coming due within the selected window.",
    group: "Compliance & Collections",
    filters: [
      {
        param: "months",
        label: "Window",
        default: "6",
        options: [
          { value: "6", label: "6 Months" },
          { value: "12", label: "One Year" },
          { value: "24", label: "Two Years" },
          { value: "36", label: "Three Years" },
        ],
      },
    ],
    columns: [
      { key: "client", label: "Client", kind: T, sortKey: "client" },
      { key: "tax_year", label: "Tax Year", kind: T, sortKey: "tax_year" },
      { key: "form", label: "Form", kind: T, sortKey: "form" },
      { key: "balance_due_cents", label: "Balance Due", kind: M, sortKey: "balance_due", align: "right" },
      { key: "csed", label: "CSED", kind: D, sortKey: "csed" },
      { key: "in_days", label: "In Days", kind: I, sortKey: "in_days", align: "right" },
      { key: "transcript_date", label: "Transcript Date", kind: D, sortKey: "transcript_date" },
      { key: "primary_practitioner", label: "Primary Practitioner", kind: T, sortKey: "primary_practitioner" },
    ],
  },
  "csed-mismatch": {
    key: "csed-mismatch",
    title: "CSED Mismatch",
    description: "Where the app-calculated CSED differs from the IRS extension date on the transcript.",
    group: "Compliance & Collections",
    columns: [
      { key: "client", label: "Client", kind: T, sortKey: "client" },
      { key: "tax_year", label: "Tax Year", kind: T, sortKey: "tax_year" },
      { key: "form", label: "Form", kind: T, sortKey: "form" },
      { key: "calculated_csed", label: "Calculated CSED", kind: D, sortKey: "calculated_csed" },
      { key: "irs_csed", label: "IRS CSED", kind: D, sortKey: "irs_csed" },
      { key: "primary_practitioner", label: "Primary Practitioner", kind: T, sortKey: "primary_practitioner" },
    ],
  },
  "unfiled-returns": {
    key: "unfiled-returns",
    title: "Unfiled Returns",
    description: "Non-filer pivot across the last six tax years, per client and form.",
    group: "Compliance & Collections",
    layout: "unfiled",
    filters: [
      {
        param: "type",
        label: "Client type",
        default: "",
        options: [
          { value: "", label: "All" },
          { value: "I", label: "Individuals only" },
          { value: "B", label: "Business only" },
        ],
      },
    ],
  },
  "outstanding-balance": {
    key: "outstanding-balance",
    title: "Outstanding Balance",
    description: "Balances owed by client as of the last transcript download.",
    group: "Compliance & Collections",
    note: "As of last transcript download.",
    columns: [
      { key: "client", label: "Client", kind: T, sortKey: "client" },
      { key: "tax_years", label: "Tax Year(s)", kind: T },
      { key: "balance_due_cents", label: "Balance Due", kind: M, sortKey: "balance_due", align: "right" },
      { key: "primary_practitioner", label: "Primary Practitioner", kind: T, sortKey: "primary_practitioner" },
    ],
  },
  "delinquent-passports": {
    key: "delinquent-passports",
    title: "Delinquent Passports",
    description: "Clients certified seriously delinquent, and those in danger of being reported.",
    group: "Compliance & Collections",
    layout: "delinquent-passports",
  },
  "accounts-receivable": {
    key: "accounts-receivable",
    title: "Accounts Receivable",
    description: "AR aging by bucket with per-client and column totals.",
    group: "Financial",
    layout: "accounts-receivable",
  },
  "practitioner-allocation": {
    key: "practitioner-allocation",
    title: "Practitioner Allocation",
    description: "How clients are distributed across practitioners, with case status.",
    group: "Firm / Practitioner",
    columns: [
      { key: "practitioner", label: "Practitioner", kind: T, sortKey: "practitioner" },
      { key: "client", label: "Client", kind: T, sortKey: "client" },
      { key: "status", label: "Status", kind: T, sortKey: "status" },
    ],
  },
  "cdp-finder": {
    key: "cdp-finder",
    title: "CDP Finder",
    description: "TC 971 collection-due-process letters issued, grouped by taxpayer.",
    group: "Compliance & Collections",
    columns: [
      { key: "client", label: "Taxpayer", kind: T, sortKey: "client" },
      { key: "trans_date", label: "Trans Date", kind: D, sortKey: "trans_date" },
      { key: "fyp_list", label: "Form/Year", kind: T },
      { key: "primary_practitioner", label: "Primary Practitioner", kind: T, sortKey: "primary_practitioner" },
    ],
  },
  kwong: {
    key: "kwong",
    title: "Kwong Report",
    description: "Penalty and interest breakdown (FTF/FTP/FTD/misc/interest) per client and form.",
    group: "Compliance & Collections",
    columns: [
      { key: "client", label: "Client", kind: T, sortKey: "client" },
      { key: "status", label: "Status", kind: T },
      { key: "form", label: "Form", kind: T, sortKey: "form" },
      { key: "year", label: "Year", kind: T, sortKey: "year" },
      { key: "earliest_assessed", label: "Earliest Assessed", kind: D },
      { key: "latest_paid", label: "Latest Paid", kind: D },
      { key: "ftf_cents", label: "FTF", kind: M, align: "right" },
      { key: "ftp_cents", label: "FTP", kind: M, align: "right" },
      { key: "ftd_cents", label: "FTD", kind: M, align: "right" },
      { key: "misc_penalty_cents", label: "Misc Penalty", kind: M, align: "right" },
      { key: "interest_cents", label: "Interest", kind: M, align: "right" },
      { key: "accrued_penalty_cents", label: "Accrued Penalty", kind: M, align: "right" },
      { key: "accrued_interest_cents", label: "Accrued Interest", kind: M, align: "right" },
    ],
  },
  "fta-finder": {
    key: "fta-finder",
    title: "FTA Finder",
    description: "First-time-abatement candidates with contact details and abatable penalty.",
    group: "Compliance & Collections",
    layout: "fta-finder",
    columns: [
      { key: "client", label: "Client", kind: T, sortKey: "client" },
      { key: "address", label: "Address", kind: T },
      { key: "city", label: "City", kind: T },
      { key: "state", label: "State", kind: T },
      { key: "zip", label: "Zip", kind: T },
      { key: "phone", label: "Phone", kind: T },
      { key: "email", label: "Email", kind: T },
      { key: "spouse_email", label: "Spouse Email", kind: T },
      { key: "tax_form", label: "Tax Form", kind: T, sortKey: "tax_form" },
      { key: "year", label: "Year(s)", kind: T, sortKey: "year" },
      { key: "amount_cents", label: "Amount", kind: M, sortKey: "amount", align: "right" },
      { key: "pract_name", label: "Primary Practitioner", kind: T, sortKey: "pract_name" },
    ],
  },
};

/** Deferred / per-client cards shown on the hub but not backed by a firm-wide
 * table route here (see milestone §12). */
export const DEFERRED_CARDS: ReportDef[] = [
  {
    key: "esignatures",
    title: "E-signature Usage",
    description: "Signature-request usage over a date range.",
    group: "Firm / Practitioner",
    to: "/reports/esignatures",
  },
  // The Tax Relief Report is per-client (section picker → PDF → save to
  // Documents) and lives on each client's "Tax Relief Report" tab, so it isn't a
  // firm-wide hub report. (E-signature Purchases was a reference-app billing
  // concept with no data source here, so it's intentionally omitted.)
];

export const REPORT_GROUPS: ReportDef["group"][] = [
  "Compliance & Collections",
  "Financial",
  "Monitoring",
  "Firm / Practitioner",
];
